"use client";

import Image from "next/image";
import type { ReactNode } from "react";
import { useState } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Button } from "@/components/ui/Button";
import { ArrowRight } from "lucide-react";
import LeadCapturePopup from "@/components/forms/LeadCapturePopup";

export interface CTASectionProps {
  title?: ReactNode;
  description?: ReactNode;
  primaryLabel?: string;
  secondaryLabel?: string;
  primaryHref?: string;
  secondaryHref?: string;
  onSecondaryClick?: () => void;
}

export function CTASection({
  title = "Ready to transform your business?",
  description = "Join hundreds of enterprises that trust ACGIL to power their digital operations. Let's discuss your unique requirements.",
  primaryLabel = "Request a Free Demo",
  secondaryLabel = "Talk to an Expert",
  primaryHref = "#",
  secondaryHref = "#",
  onSecondaryClick,
}: CTASectionProps) {
  const [popupOpen, setPopupOpen] = useState(false);
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const revealClass = isVisible ? "animate-fade-up" : "opacity-0";

  return (
      <section ref={sectionRef} className="relative pt-20 pb-12 overflow-hidden bg-gradient-to-br from-white via-[#237BC5]/10 to-white md:py-20">
      <div
        className="absolute inset-0 z-0 pointer-events-none hidden md:block"
        aria-hidden="true"
        style={{
          background: "linear-gradient(to bottom, rgba(35,123,197,0.15) 0%, transparent 40%)",
        }}
      />
      <div
        className="absolute inset-0 z-0 pointer-events-none md:hidden"
        aria-hidden="true"
        style={{
          background: "linear-gradient(to bottom, rgba(35,123,197,0.18) 0%, transparent 40%)",
        }}
      />
      <div
        className="absolute inset-0 z-0 pointer-events-none hidden md:block"
        aria-hidden="true"
        style={{
          backgroundImage: "url('/images/bg-img/bg-cta.avif')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right bottom",
          backgroundSize: "auto 55%",
        }}
      />

      <div className="container relative z-10 px-6 mx-auto text-center">
        <div className="max-w-4xl mx-auto">
          <div
            className={`inline-block mb-6 ${revealClass}`}
          >
            <span className="text-[11px] font-medium tracking-[0.14em] uppercase text-[#237BC5] border border-[#237BC5] rounded-full px-4 py-1.5">
              Enterprise Ready
            </span>
          </div>

          <h2
            className={`text-4xl md:text-5xl font-extrabold text-[#0d1b2e] mb-5 leading-[1.1] tracking-tight ${revealClass}`}
            style={{ animationDelay: "0.05s" }}
          >
            {title}
          </h2>

          <p
            className={`text-base md:text-lg text-slate-500 mb-9 max-w-lg mx-auto leading-relaxed ${revealClass}`}
            style={{ animationDelay: "0.12s" }}
          >
            {description}
          </p>

          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-3.5 ${revealClass}`}
            style={{ animationDelay: "0.2s" }}
          >
            <Button
              size="lg"
              className="group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-[#237BC5] to-[#1a68ab] px-7 text-xs font-black uppercase tracking-normal text-white shadow-lg shadow-[#08265a]/25 transition-[filter,background-color] hover:brightness-110 w-full sm:w-auto"
              onClick={() => setPopupOpen(true)}
              asChild
            >
              <span className="relative z-10 flex items-center gap-2">
                {primaryLabel}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Button>
            {secondaryLabel ? (
              <Button
                variant="outline"
                size="lg"
                className="group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden rounded-full border border-[#237BC5]/20 bg-white/50 px-7 text-xs font-black uppercase tracking-normal text-[#237BC5] shadow-sm transition-[background-color,border-color] hover:border-[#237BC5]/40 hover:bg-[#237BC5]/5 w-full sm:w-auto"
                onClick={() => {
                  if (onSecondaryClick) {
                    onSecondaryClick();
                  } else {
                    const target = document.getElementById('contact-form');
                    if (target) {
                      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }
                }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  {secondaryLabel}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mt-4 md:hidden">
          <Image
            src="/images/bg-img/bg-cta.avif"
            alt=""
            width={677}
            height={369}
            className="w-full h-auto object-contain"
          />
        </div>
      </div>

      <LeadCapturePopup
        open={popupOpen}
        onOpenChange={setPopupOpen}
        triggerLabel="Request Free Demo"
        headline="Schedule Your Free ERP Consultation"
        subheadline="Explore how ACGIL's 30+ years of ERP expertise can streamline your operations. Our experts will walk you through a tailored solution."
      />
    </section>
  );
}
